/**
 * Usage Service - API calls for Promo Usage
 * 
 * Handles all usage-related API operations:
 * - List promo usages with filtering and pagination
 */

import { PROMOS_ENDPOINTS } from './endpoints';
import secureApi from '../../utils/secureApi';
import {
  PromoUsage,
  UsageListResponse,
  UsageListParams,
} from './types/usageTypes';

/**
 * Transform API response to PromoUsage interface
 */
const transformUsageResponse = (item: any): PromoUsage => {
  return {
    _id: item._id || item.id,
    promoId: item.promoId,
    promoName: item.promoName,
    promoCode: item.promoCode,
    userId: item.userId,
    // Map customerName/customerEmail to userName/userEmail
    userName: item.customerName || item.userName,
    userEmail: item.customerEmail || item.userEmail,
    orderId: item.orderId,
    transactionId: item.transactionId,
    domain: item.domain,
    // Map originalAmount to amount (Amount before discount)
    amount: item.originalAmount || item.amount,
    // Map discountValue to discountAmount
    discountAmount: item.discountValue || item.discountAmount,
    // Map discountedAmount (Final amount after discount)
    discountedAmount: item.discountedAmount,
    usedAt: item.usedAt,
    status: item.status,
  };
};

/**
 * Get promo usages with filtering and pagination
 */
export const getPromoUsages = async (params: UsageListParams = {}): Promise<UsageListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add query parameters
    if (params.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.promoId) queryParams.append('promoId', params.promoId);
    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.startAt) queryParams.append('startAt', params.startAt);
    if (params.endAt) queryParams.append('endAt', params.endAt);
    
    const endpoint = `${PROMOS_ENDPOINTS.USAGE.LIST}?${queryParams.toString()}`;
    const response = await secureApi.get(endpoint);
    
    // Transform API response
    const data = response.data?.data || response.data;
    
    if (data.results && Array.isArray(data.results)) {
      const results = data.results.map(transformUsageResponse);
      const totalCount = data.totalCount || 0;
      
      return {
        results,
        totalCount,
      };
    } else if (data.paginatedResults && Array.isArray(data.paginatedResults)) {
      // Handle paginated response format
      const results = data.paginatedResults.map(transformUsageResponse);
      const totalCount = data.totalCount || 0;
      
      return {
        results,
        totalCount,
      };
    } else if (Array.isArray(data)) {
      // Handle array response
      const results = data.map(transformUsageResponse);
      return {
        results,
        totalCount: results.length,
      };
    } else {
      // Handle unexpected format
      return {
        results: [],
        totalCount: 0,
      };
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to fetch promo usages. Please try again.';
    throw new Error(errorMessage);
  }
};
